import React, { useEffect, useState } from 'react';
import { drapcodeIcon } from '../../../common/custom/icon-library';
import showAlert from '../../../common/utils/alert';
import {
  aiModelEngines,
  CLAUDE_AI,
  claudeAiEngines,
  DEEPSEEK_AI,
  deepseekAiEngines,
  OPEN_AI,
  openAiEngines,
} from 'drapcode-constant';
import { convertConstantCaseToTitleCase } from '../../../common/utils/api_utils';
import CustomSelect from '../../../common/formElements/CustomSelect';
import FormFieldWithLabel from '../../common/FormFieldWithLabel';
import CustomRadio from '../../../common/ui/CustomRadio';

export const AiModelSettingForm = ({
  projectId,
  version,
  aiModelType,
  aiModelConfig,
  updateAiModelConfig,
}) => {
  const getAiModelEngine = (aiModelType) => {
    switch (aiModelType) {
      case OPEN_AI:
        return openAiEngines.GPT_4O_2024_05_13;
      case CLAUDE_AI:
        return claudeAiEngines.CLAUDE_SONNET_4_0;
      case DEEPSEEK_AI:
        return deepseekAiEngines.DEEP_SEEK_V3;
      default:
        return '';
    }
  };

  const aiModelTypeLabel = convertConstantCaseToTitleCase(aiModelType, 'AI');
  const aiModelEngineOptions = aiModelEngines.filter((engine) => engine.modelType === aiModelType);
  let aiModelSecKey = '';
  let aiModelTemp = 0.7;
  let aiModelEngine = getAiModelEngine(aiModelType);
  let aiModelTopP = 1;
  let aiModelIsEnabled = true;
  if (aiModelConfig) {
    const { setting, isEnabled } = aiModelConfig;
    aiModelIsEnabled = isEnabled;
    if (setting) {
      const { modelEngine, secretKey, temperature, topP } = setting;
      if (modelEngine) aiModelEngine = modelEngine;
      if (secretKey) aiModelSecKey = secretKey;
      if (temperature) aiModelTemp = temperature;
      if (topP) aiModelTopP = topP;
    }
  }
  if (!aiModelSecKey) {
    aiModelIsEnabled = false;
  }
  const [engine, setEngine] = useState(aiModelEngine);
  const [temperature, setTemperature] = useState(aiModelTemp);
  const [topP, setTopP] = useState(aiModelTopP);
  const [secretKey, setSecretKey] = useState(aiModelSecKey);
  const [isEnabled, setIsEnabled] = useState(aiModelIsEnabled);
  const [isDefault, setIsDefault] = useState(false);

  useEffect(() => {
    if (aiModelConfig) {
      const { isDefault } = aiModelConfig;
      setIsDefault(isDefault);
    } else {
      setIsDefault(false);
    }
  }, [aiModelType, aiModelConfig]);

  const renderSubmitBtn = () => {
    const hasRequiredFields = !!(engine && temperature && topP);
    if (hasRequiredFields) {
      return (
        <button className={`btn btn-theme-submit btn-md`} onClick={handleSubmit}>
          <i className={`wb ${drapcodeIcon('save')}`}></i>&nbsp;Update&nbsp;
        </button>
      );
    } else {
      return (
        <button className={`btn btn-disabled btn-md`} disabled>
          <i className={`wb ${drapcodeIcon('save')}`}></i>&nbsp;Update&nbsp;
        </button>
      );
    }
  };

  const handleIsEnabledCheckboxChange = (event) => {
    const { checked } = event.target;
    setIsEnabled(checked);
  };
  const handleIsDefaultCheckboxChange = (event) => {
    const { checked } = event.target;
    setIsDefault(checked);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log(
      '🚀 ~ AI Model Config ~ handleSubmit ~ engine:',
      engine,
      'temperature:',
      temperature,
      'topP:',
      topP,
      'secretKey:',
      secretKey,
      'projectId:',
      projectId,
      'version:',
      version,
      'aiModelType:',
      aiModelType,
      'isEnabled:',
      isEnabled,
    );
    const preConfirm = () =>
      updateAiModelConfig({
        projectId,
        version,
        modelEngine: engine,
        temperature,
        topP,
        secretKey,
        modelType: aiModelType,
        isEnabled,
        isDefault,
      })
        .then(({ data }) => {
          if (data.success === false) {
            console.log('🚀 ~ AI Model Config ~ data.message:', data.message);
          } else {
            const aiModelConfigs = data.aiModelConfigs;
            const aiModelConfig =
              aiModelConfigs && aiModelConfigs.find((amc) => amc.modelType === aiModelType);
            const { setting, isEnabled ,isDefault} = aiModelConfig || {};
            const { modelEngine, secretKey, temperature, topP } = setting || {};
            if (modelEngine) setEngine(modelEngine);
            if (secretKey) setSecretKey(secretKey);
            if (temperature) setTemperature(temperature);
            if (topP) setTopP(topP);
            // aiModelIsEnabled = isEnabled;
            setIsEnabled(isEnabled);
            setIsDefault(isDefault);
          }
        })
        .catch((error) => {
          console.log('🚀 ~ AI Model Config ~ handleSubmit ~ error:', error);
        });
    showAlert("Do you want to update the AI Model's configuration?", preConfirm, 'Yes, Update it!');
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12 col-lg-12 col-sm-12">
          <form className="form-horizontal">
            <div className="row mx-auto">
              <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12">
                <div className="form-group">
                  <FormFieldWithLabel
                    cta={false}
                    label="Secret Key"
                    icon="password"
                    infoIcon={true}
                    infoDescription={`Enter ${aiModelTypeLabel} Secret Key`}
                  >
                    <input
                      type="password"
                      name="secretKey"
                      className="form-control"
                      placeholder={`Enter ${aiModelTypeLabel} Secret Key`}
                      value={secretKey}
                      onChange={(e) => setSecretKey(e.target.value)}
                    />
                  </FormFieldWithLabel>
                </div>
                <div className="form-group">
                  <FormFieldWithLabel
                    cta={false}
                    label="AI Model Engine"
                    icon="select"
                    infoIcon={true}
                    infoDescription={`Select the ${aiModelTypeLabel} Model Engine`}
                  >
                    <CustomSelect
                      name="engine"
                      isMulti={false}
                      options={aiModelEngineOptions}
                      value={engine}
                      onChange={(name, value) => setEngine(value)}
                      placeholder={`Select ${aiModelTypeLabel} Engine`}
                    />
                  </FormFieldWithLabel>
                </div>
                <div className="form-group">
                  <FormFieldWithLabel
                    cta={false}
                    label="Temperature"
                    icon="number"
                    infoIcon={true}
                    infoDescription={`Enter ${aiModelTypeLabel} Temperature`}
                  >
                    <input
                      type="number"
                      step="0.01"
                      name="temperature"
                      className="form-control"
                      placeholder={`Enter ${aiModelTypeLabel} Temperature`}
                      value={temperature}
                      onChange={(e) => setTemperature(e.target.value)}
                    />
                  </FormFieldWithLabel>
                </div>
                <div className="form-group">
                  <FormFieldWithLabel
                    cta={false}
                    label="Nucleus Sampling (Top P)"
                    icon="number"
                    infoIcon={true}
                    infoDescription={`Enter ${aiModelTypeLabel} Nucleus Sampling (Top P)`}
                  >
                    <input
                      type="number"
                      step="0.01"
                      name="topP"
                      className="form-control"
                      placeholder={`Enter ${aiModelTypeLabel} Nucleus Sampling (Top P)`}
                      value={topP}
                      onChange={(e) => setTopP(e.target.value)}
                    />
                  </FormFieldWithLabel>
                </div>
                <div className="form-group">
                  <FormFieldWithLabel
                    cta={false}
                    label="Enable AI Model"
                    icon="toggle"
                    infoIcon={true}
                    infoDescription={`Enable ${aiModelTypeLabel}`}
                  >
                    <div className="form-check">
                      <CustomRadio label={`Enable ${aiModelTypeLabel}`} isRadio={false}>
                        <input
                          name="isEnabled"
                          type="checkbox"
                          className="form-check-input"
                          checked={isEnabled}
                          onChange={handleIsEnabledCheckboxChange}
                        />
                      </CustomRadio>
                    </div>
                  </FormFieldWithLabel>
                  <FormFieldWithLabel
                    cta={false}
                    label="Set Default AI Model"
                    icon="toggle"
                    infoIcon={true}
                    infoDescription={`Set ${aiModelTypeLabel} as default — no need to select it each time.`}
                  >
                    <div className="form-check">
                      <CustomRadio label={`Set As Default Model`} isRadio={false}>
                        <input
                          name="isDefault"
                          type="checkbox"
                          className="form-check-input"
                          checked={isDefault}
                          onChange={handleIsDefaultCheckboxChange}
                        />
                      </CustomRadio>
                    </div>
                  </FormFieldWithLabel>
                </div>
              </div>
              <div className="col-md-12 col-lg-12 mt-2 text-right">
                <div className="row mr-2">
                  <div className="col-md-12 col-lg-12 col-sm-12 col-xs-12 text-right d-flex justify-content-end">
                    {renderSubmitBtn()}
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
